import { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigation } from '../context/NavigationContext';
import { apiClient } from '../api/client';
import { useIdeaDashboard, useUpdateIdea, useDeleteIdea } from '../hooks/ideas/useIdeas';
import { mapIdeaDashboard } from '../utils/dashboardMappers';
import { PageLoading } from './common/PageLoading';
import { PageError } from './common/PageError';
import { confirmAction, showError, showSuccess } from '../lib/alerts';
import EditIdeaModal from './EditIdeaModal';
import {
  ArrowLeft,
  Brain,
  TrendingUp,
  Coins,
  MessageSquare,
  FileText,
  Calendar,
  Edit,
  Share2,
  Rocket,
  Activity,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Compass,
  Clock,
  ChevronRight,
  Sparkles,
  Download,
  Upload,
  BarChart3,
  User,
  Send,
  Hash,
  Pin,
  Trash2
} from 'lucide-react';

const getMemberPhoto = (name?: any): string | null => {
  if (!name || typeof name !== 'string') return null;
  const lower = name.toLowerCase();
  if (lower.includes('joão') || lower.includes('joao')) {
    return 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('maria')) {
    return 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('pedro')) {
    return 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('ana')) {
    return 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('lucas')) {
    return 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('carla')) {
    return 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('carlos')) {
    return 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('marcos')) {
    return 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&auto=format&fit=crop&q=80';
  }
  const fallbacks = [
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&auto=format&fit=crop&q=80'
  ];
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return fallbacks[sum % fallbacks.length];
};

const COLUMN_INFO: { [key: string]: { label: string; bg: string; text: string; border: string } } = {
  capturadas: { label: '💡 Capturada', bg: 'bg-neutral-50', text: 'text-neutral-600', border: 'border-neutral-200' },
  avaliacao: { label: '🔍 Em Análise', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  pesquisa: { label: '📋 Refinamento', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  mvp: { label: '💰 Business Case', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  aprovadas: { label: '✅ Aprovada', bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200' },
  desenvolvimento: { label: '🚀 Em Desenvolvimento', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  entregues: { label: '🎉 Entregue', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  arquivadas: { label: '📦 Arquivada', bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' }
};

interface Comment {
  id: number;
  author: string;
  role: string;
  photo: string | null;
  text: string;
  time: string;
  timelineGroup: 'Hoje' | 'Ontem' | 'Esta Semana' | 'Este Mês' | 'Mais Antigos';
  type: 'comment' | 'idea' | 'decision' | 'document' | 'image' | 'indicator' | 'deploy' | 'event' | 'warning' | 'approval' | 'rejection' | 'pin' | 'ai' | 'achievement' | 'meeting' | 'system' | 'suggestion';
  votes: number;
  likedByCurrentUser?: boolean;
  replies?: Array<{
    id: number;
    author: string;
    role: string;
    photo: string | null;
    text: string;
    time: string;
    likedByCurrentUser?: boolean;
    votes?: number;
    replies?: Array<{
      id: number;
      author: string;
      role: string;
      photo: string | null;
      text: string;
      time: string;
    }>;
  }>;
  tags?: string[];
  decidedBy?: string;
  approvedBy?: string;
  impact?: 'Alto' | 'Médio' | 'Baixo';
  relationships?: string[];
  isPinned?: boolean;
  voting?: {
    agree: number;
    disagree: number;
    greatIdea: number;
    userVote?: 'agree' | 'disagree' | 'greatIdea';
  };
  attachments?: Array<{
    name: string;
    size: string;
    type: 'pdf' | 'image' | 'xlsx' | 'docx' | 'link';
    url?: string;
  }>;
  indicator?: {
    metric: string;
    value: string;
    trend: 'up' | 'down' | 'neutral';
  };
  corporateReactions?: {
    agree: number;
    excellent: number;
    goodIdea: number;
    attention: number;
    approved: number;
    review: number;
    priority: number;
    userReactions?: string[];
  };
}

export default function IdeiaWorkspaceView({ setCurrentView: setAppView }: { setCurrentView: (view: any) => void }) {
  const { globalFilters, setGlobalFilters } = useNavigation();

  const ideaId = String(globalFilters.ideaId || '');

  // Dashboard API query
  const { data: rawDashboard, isLoading, isError, error } = useIdeaDashboard(ideaId);
  const updateIdeaMutation = useUpdateIdea();
  const deleteIdeaMutation = useDeleteIdea();

  // Mapped dashboard state
  const dashboard = useMemo(() => mapIdeaDashboard(rawDashboard), [rawDashboard]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Derived data from dashboard
  const idea = useMemo(() => {
    if (!dashboard) return null;
    return {
      id: dashboard.id,
      title: dashboard.title || 'Nova Ideia',
      description: dashboard.description || '',
      status: dashboard.status || 'avaliacao',
      category: dashboard.category || 'Inteligência Artificial & Dados',
      department: dashboard.department || 'Pesquisa & Desenvolvimento',
      company: dashboard.company || 'Empresa',
      creator: dashboard.author?.name || dashboard.createdBy || 'Criador',
      owner: dashboard.author?.name || 'Responsável',
      score: dashboard.score || 0,
      roi: dashboard.roi || 'N/A',
      impact: dashboard.impact || 'Médio',
      effort: dashboard.effort || 'Médio',
      complexity: dashboard.complexity || 'Média',
      risk: dashboard.risk || 'Baixo',
      estimatedTime: dashboard.estimatedTime || '3 meses',
      strategicAlignment: dashboard.strategicAlignment || '80%',
      createdAt: dashboard.createdAt || new Date().toISOString(),
      updatedAt: dashboard.updatedAt || new Date().toISOString(),
      problem: dashboard.problem || 'Problema a ser definido.',
      solution: dashboard.solution || 'Solução a ser definida.',
      objective: dashboard.objective || 'Objetivo a ser definido.',
      outcomes: dashboard.outcomes || 'Resultados esperados a serem definidos.',
      benefits: dashboard.benefits || ['Benefício a ser definido.'],
      canvas: dashboard.canvas || { problem: '', solution: '', metrics: '', valueProp: '', advantage: '', channels: '', segments: '', costs: '', revenues: '' },
      tags: dashboard.tags || [],
      priority: dashboard.priority || 'Média',
    };
  }, [dashboard]);

  // Files from dashboard
  const files = useMemo(() => {
    if (!dashboard?.files) return [];
    return dashboard.files.map((f: any) => ({
      id: f.id,
      name: f.name || 'Arquivo',
      size: f.size || '0 KB',
      type: f.type || 'file',
      addedBy: f.addedBy || 'Sistema',
      date: f.date || '',
    }));
  }, [dashboard]);

  // History from dashboard
  const historyLogs = useMemo(() => {
    if (!dashboard?.history) return [];
    return dashboard.history.map((h: any) => ({
      id: h.id,
      action: h.action,
      user: h.user,
      time: h.time,
    }));
  }, [dashboard]);

  // Comments from dashboard (with fallback)
  const [comments, setComments] = useState<Comment[]>([]);

  // Sync comments from dashboard when data loads
  const prevDashboardId = useRef<string | null>(null);
  if (dashboard && dashboard.id !== prevDashboardId.current) {
    prevDashboardId.current = dashboard.id;
    if (dashboard.comments && dashboard.comments.length > 0) {
      setComments(dashboard.comments.map((c: any) => ({
        id: c.id || Date.now(),
        author: c.author || 'Usuário',
        role: c.role || 'Colaborador',
        photo: getMemberPhoto(c.author),
        text: c.text || '',
        time: c.time ? new Date(c.time).toLocaleString('pt-BR') : 'Agora mesmo',
        timelineGroup: 'Hoje' as const,
        type: 'comment' as const,
        votes: 0,
        likedByCurrentUser: false,
        tags: [],
        corporateReactions: { agree: 0, excellent: 0, goodIdea: 0, attention: 0, approved: 0, review: 0, priority: 0, userReactions: [] },
        replies: []
      })));
    }
  }

  // Active Tab
  const [activeTab, setActiveTab] = useState<string>('overview');

  // IA status
  const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState<any | null>(null);

  // New Comment state
  const [newCommentText, setNewCommentText] = useState('');
  const [commentType, setCommentType] = useState<Comment['type']>('comment');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeReactionPickerId, setActiveReactionPickerId] = useState<number | null>(null);
  const [selectedNewCommentTags, setSelectedNewCommentTags] = useState<string[]>([]);
  const [discussionFilterTag, setDiscussionFilterTag] = useState<string | null>(null);
  const [discussionFilterType, setDiscussionFilterType] = useState<string>('all');
  const [activeReplyBoxCommentId, setActiveReplyBoxCommentId] = useState<number | null>(null);
  const [newReplyTexts, setNewReplyTexts] = useState<{ [commentId: number]: string }>({});

  // Project state
  const [isConvertedToProject, setIsConvertedToProject] = useState(false);
  const [convertedProjectData, setConvertedProjectData] = useState<any | null>(null);

  // Form edit status
  const [isEditingStatus, setIsEditingStatus] = useState(false);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Dynamic Social Feed Filtering logic
  const filteredComments = useMemo(() => {
    return comments.filter(c => {
      if (discussionFilterType !== 'all' && c.type !== discussionFilterType) return false;
      if (discussionFilterTag && (!c.tags || !c.tags.includes(discussionFilterTag))) return false;
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesAuthor = c.author.toLowerCase().includes(query);
        const matchesText = c.text.toLowerCase().includes(query);
        const matchesRole = c.role.toLowerCase().includes(query);
        const matchesTags = c.tags ? c.tags.some(t => t.toLowerCase().includes(query)) : false;
        if (!matchesAuthor && !matchesText && !matchesRole && !matchesTags) return false;
      }
      return true;
    });
  }, [comments, discussionFilterType, discussionFilterTag, searchQuery]);

  // Dynamic Popular/Trending Hashtags computation
  const popularTags = useMemo(() => {
    const counts: { [tag: string]: number } = {};
    comments.forEach(c => {
      if (c.tags) c.tags.forEach(t => { counts[t] = (counts[t] || 0) + 1; });
    });
    return Object.entries(counts).map(([tag, count]) => ({ tag, count })).sort((a, b) => b.count - a.count);
  }, [comments]);

  // Share handler
  const handleShareComment = (comment: Comment) => {
    navigator.clipboard.writeText(`${window.location.origin}/ideas/${ideaId}/discussion/${comment.id}`);
    showToast('🔗 Link da publicação copiado para a área de transferência!');
  };

  // Handle Comment Submission
  const handleAddComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCommentText.trim()) return;
    const textHashtags = newCommentText.match(/#\w+/g) || [];
    const combinedTags = Array.from(new Set([...selectedNewCommentTags, ...textHashtags]));
    const newComment: Comment = {
      id: Date.now(),
      author: 'João Silva',
      role: 'Senior Backend Engineer',
      photo: getMemberPhoto('João Silva'),
      text: newCommentText,
      time: 'Agora mesmo',
      timelineGroup: 'Hoje',
      type: commentType,
      votes: 0,
      likedByCurrentUser: false,
      tags: combinedTags,
      corporateReactions: { agree: 0, excellent: 0, goodIdea: 0, attention: 0, approved: 0, review: 0, priority: 0, userReactions: [] },
      replies: []
    };
    if (commentType === 'suggestion') {
      newComment.voting = { agree: 0, disagree: 0, greatIdea: 0, userVote: undefined };
    }
    setComments(prev => [newComment, ...prev]);
    setNewCommentText('');
    setCommentType('comment');
    setSelectedNewCommentTags([]);
  };

  // Pin / Unpin Activity
  const handleTogglePin = (id: number) => {
    setComments(prev => prev.map(c => {
      if (c.id === id) {
        const isPinned = !c.isPinned;
        showToast(isPinned ? '📌 Atividade fixada no topo do feed!' : '📌 Atividade desfixada!');
        return { ...c, isPinned };
      }
      return c;
    }));
  };

  // Structured Voting Handler
  const handleSuggestVote = (id: number, option: 'agree' | 'disagree' | 'greatIdea') => {
    setComments(prev => prev.map(c => {
      if (c.id === id && c.voting) {
        const currentVote = c.voting.userVote;
        const newVoting = { ...c.voting };
        if (currentVote) newVoting[currentVote] = Math.max(0, newVoting[currentVote] - 1);
        if (currentVote === option) {
          newVoting.userVote = undefined;
          showToast('🗳 Voto removido com sucesso!');
        } else {
          newVoting[option] += 1;
          newVoting.userVote = option;
          showToast(`🗳 Voto "${option === 'agree' ? 'Concordo' : option === 'disagree' ? 'Discordo' : 'Excelente Ideia!'}" registrado!`);
        }
        return { ...c, voting: newVoting };
      }
      return c;
    }));
  };

  // Corporate Emoji Reactions Handler
  const handleCorporateReact = (id: number, reaction: string) => {
    setComments(prev => prev.map(c => {
      if (c.id === id) {
        const currentReactions = c.corporateReactions || { agree: 0, excellent: 0, goodIdea: 0, attention: 0, approved: 0, review: 0, priority: 0, userReactions: [] };
        const userReactions = [...(currentReactions.userReactions || [])];
        const isSelected = userReactions.includes(reaction);
        const updatedReactions = { ...currentReactions };
        const delta = isSelected ? -1 : 1;
        if (reaction === 'agree') updatedReactions.agree = Math.max(0, updatedReactions.agree + delta);
        else if (reaction === 'excellent') updatedReactions.excellent = Math.max(0, updatedReactions.excellent + delta);
        else if (reaction === 'goodIdea') updatedReactions.goodIdea = Math.max(0, updatedReactions.goodIdea + delta);
        else if (reaction === 'attention') updatedReactions.attention = Math.max(0, updatedReactions.attention + delta);
        else if (reaction === 'approved') updatedReactions.approved = Math.max(0, updatedReactions.approved + delta);
        else if (reaction === 'review') updatedReactions.review = Math.max(0, updatedReactions.review + delta);
        else if (reaction === 'priority') updatedReactions.priority = Math.max(0, updatedReactions.priority + delta);
        updatedReactions.userReactions = isSelected ? userReactions.filter(r => r !== reaction) : [...userReactions, reaction];
        return { ...c, corporateReactions: updatedReactions };
      }
      return c;
    }));
    setActiveReactionPickerId(null);
  };

  // Quick Action: Convert Suggestion to Formal Decision
  const handleConvertSuggestionToDecision = (id: number) => {
    setComments(prev => prev.map(c => {
      if (c.id === id) {
        showToast('⚡ Sugestão convertida em Decisão Oficial homologada!');
        return { ...c, type: 'decision' as const, decidedBy: 'João Silva', approvedBy: 'Maria Santos (Product Manager)', impact: 'Médio' as const, text: `⚡ Decisão Homologada a partir de sugestão: ${c.text.replace(/💡 Sugestão de Validação: |💡 /g, '')}` };
      }
      return c;
    }));
  };

  // Quick Action: Convert Activity to Task
  const handleConvertActivityToTask = () => {
    showToast('🚀 Tarefa gerada automaticamente a partir da atividade e vinculada ao Roadmap!');
  };

  // Toggle Like state
  const handleToggleLikeComment = (id: number) => {
    setComments(prev => prev.map(c => {
      if (c.id === id) {
        const liked = !c.likedByCurrentUser;
        return { ...c, likedByCurrentUser: liked, votes: liked ? c.votes + 1 : Math.max(0, c.votes - 1) };
      }
      return c;
    }));
  };

  // Add a reply to a comment
  const handleAddReply = (commentId: number) => {
    const text = newReplyTexts[commentId];
    if (!text || !text.trim()) return;
    const newReply = { id: Date.now(), author: 'João Silva', role: 'Senior Backend Engineer', photo: getMemberPhoto('João Silva'), text: text.trim(), time: 'Agora mesmo', votes: 0, likedByCurrentUser: false };
    setComments(prev => prev.map(c => {
      if (c.id === commentId) return { ...c, replies: [...(c.replies || []), newReply] };
      return c;
    }));
    setNewReplyTexts(prev => ({ ...prev, [commentId]: '' }));
  };

  // File upload simulation
  const [localFiles, setLocalFiles] = useState<any[]>([]);
  const allFiles = useMemo(() => [...localFiles, ...files], [localFiles, files]);

  const handleFileUpload = (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const newFile = { id: Date.now(), name: file.name, size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`, type: file.name.split('.').pop() || 'file', addedBy: 'João Silva', date: new Date().toISOString().split('T')[0] };
    setLocalFiles(prev => [...prev, newFile]);
  };

  // AI Analysis Trigger
  const handleAiAnalysis = () => {
    setIsAiAnalyzing(true);
    setTimeout(() => {
      setAiAnalysisResult({
        summary: 'A ideia propõe resolver uma das maiores dores operacionais do setor financeiro de PMEs: a imprevisibilidade de fluxo de caixa em curto prazo. O alinhamento com a infraestrutura ERP existente é de altíssimo nível (Cyzor Core).',
        scoreBreakdown: { originality: 82, feasibility: 95, strategicAlignment: 98, marketDemand: 90, complexity: 65 },
        suggestions: ['Utilizar modelos pré-treinados finetunados (como Llama-3 ou Gemma-2B-IT) localmente para processamento de linguagem natural e previsão de datas.', 'Criar uma etapa de triagem de dados nulos antes de alimentar o modelo neural para evitar distorções estatísticas.', 'Oferecer os primeiros 30 dias gratuitamente para base de testes para maximizar a captação de dados de validação.'],
        riscos: ['Qualidade de dados de entrada variáveis por empresa cliente.', 'Consumo excessivo de processamento de nuvem sem monetização correspondente estruturada.'],
        improvements: 'Integrar relatórios em formato de áudio sintético no aplicativo móvel da Cyzor (Cyzor Audio Insights) para aumentar engajamento de diretores.',
        productsRelated: ['Cyzor Financeiro Premium', 'Cyzor CRM Inteligente'],
        featuresRelated: ['Faturamento inteligente', 'Automatização de Cobrança WhatsApp']
      });
      setIsAiAnalyzing(false);
    }, 2000);
  };

  // Convert to Project flow
  const handleConvertToProject = async () => {
    const { data: project } = await apiClient.post('/projects', {
      name: `Projeto - ${idea?.title || 'Nova ideia'}`,
      description: idea?.description || null,
      status: 'PLANNING',
    });
    setIsConvertedToProject(true);
    setConvertedProjectData({
      id: project.id,
      projectName: `Projeto - ${idea?.title}`,
      status: 'Iniciação',
      progress: 10,
      sprint: 'Sprint 01 - Setup de Arquitetura',
      manager: 'João Silva',
      members: ['João Silva', 'Maria Santos', 'Pedro Santos'],
      code: `PROJ-IDEA-${(idea?.id || '').toString().padStart(3, '0')}`
    });
  };

  // Handle status update via mutation
  const handleUpdateStatus = (newStatus: string) => {
    if (ideaId) {
      updateIdeaMutation.mutate({ id: ideaId, data: { status: newStatus } });
    }
    setIsEditingStatus(false);
    showToast(`Status alterado para "${COLUMN_INFO[newStatus]?.label || newStatus}"`);
  };

  const handleDeleteIdea = async () => {
    if (!ideaId) return;
    const confirmed = await confirmAction('Excluir ideia', 'Tem certeza que deseja excluir esta ideia? Esta ação remove o registro da visão atual.');
    if (!confirmed) return;

    deleteIdeaMutation.mutate(ideaId, {
      onSuccess: () => {
        setGlobalFilters({ ...globalFilters, ideaId: undefined });
        setAppView('ideias');
        showSuccess('Ideia excluída com sucesso!');
      },
      onError: (error: any) => {
        showError(error?.message || 'Erro ao excluir a ideia.');
      },
    });
  };

  const currentStatusInfo = COLUMN_INFO[idea?.status] || COLUMN_INFO.avaliacao;

  // Loading / Error states
  if (isLoading) return <PageLoading message="Carregando dados da ideia..." />;
  if (isError) return <PageError message={(error as any)?.message || 'Erro ao carregar dados da ideia'} onRetry={() => window.location.reload()} />;
  if (!idea) return <PageLoading message="Preparando workspace..." />;

  const tabAnimation = {
    initial: { opacity: 0, y: 6 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -6 },
    transition: { duration: 0.2 }
  };

  return (
    <div className="w-full mx-auto pb-12 flex flex-col gap-8 animate-in fade-in duration-500 relative px-2 sm:px-4 md:px-8 text-left">
      
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          BREADCRUMBS & TOP BAR
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="flex justify-between items-center text-xs font-bold text-neutral-400">
        <div className="flex items-center gap-3">
          <button onClick={() => { setGlobalFilters({ ...globalFilters, ideaId: undefined }); setAppView('ideias'); }} className="hover:text-neutral-900 flex items-center gap-1 transition-colors">
            Banco de Ideias
          </button>
          <ChevronRight size={10} />
          <span className="text-neutral-900 font-black">{idea.title}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-neutral-400 font-medium">Código de Inovação:</span>
          <span className="text-[10px] text-neutral-800 font-extrabold px-1.5 py-0.5 bg-neutral-100 border border-neutral-200 rounded">
            CYZ-INOV-{(idea.id || '').toString().padStart(3, '0')}
          </span>
        </div>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          PREMIUM HERO HEADER
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="bg-white border border-[#0F172A0A] rounded-[32px] p-6 sm:p-8 shadow-[0_4px_24px_rgba(15,23,42,0.02)] flex flex-col gap-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-start gap-4 sm:gap-5">
            <button onClick={() => { setGlobalFilters(prev => ({ ...prev, ideaId: undefined })); setAppView('ideias'); }} className="p-2.5 rounded-xl hover:bg-neutral-50 text-neutral-400 hover:text-neutral-900 transition-all border border-neutral-100/50 mr-1 mt-1 cursor-pointer" title="Voltar para a Lista de Ideias">
              <ArrowLeft size={16} strokeWidth={2.5} />
            </button>
            <div className="w-14 h-14 rounded-2xl bg-neutral-900 text-white border border-neutral-800 flex items-center justify-center font-black text-xl shadow-inner shrink-0">
              {idea.title ? idea.title.substring(0, 2).toUpperCase() : 'ID'}
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-xl sm:text-2xl font-black text-[#0F172A] tracking-tight">{idea.title}</h1>
                <div className="relative">
                  <button onClick={() => setIsEditingStatus(!isEditingStatus)} className={`px-3 py-1 rounded-full text-[10px] font-bold border cursor-pointer hover:brightness-95 transition-all flex items-center gap-1.5 ${currentStatusInfo.bg} ${currentStatusInfo.text} ${currentStatusInfo.border}`}>
                    {currentStatusInfo.label} <ChevronRight size={10} className="rotate-90" />
                  </button>
                  <AnimatePresence>
                    {isEditingStatus && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setIsEditingStatus(false)} />
                        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 5 }} className="absolute left-0 mt-1.5 w-56 bg-white border border-neutral-100 rounded-xl shadow-lg py-1.5 z-20">
                          {Object.entries(COLUMN_INFO).map(([key, col]) => (
                            <button key={key} onClick={() => handleUpdateStatus(key)} className="w-full text-left px-4 py-2 hover:bg-neutral-50 text-xs font-semibold text-neutral-700 flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full bg-neutral-400`} /> {col.label}
                            </button>
                          ))}
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
                <span className="text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600">{idea.category}</span>
                <span className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full border ${idea.priority === 'Alta' || idea.priority === 'HIGH' ? 'bg-rose-50 text-rose-600 border-rose-100' : idea.priority === 'Média' || idea.priority === 'MEDIUM' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-slate-50 text-slate-500 border-slate-100'}`}>
                  Prioridade: {idea.priority}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-y-1.5 gap-x-4 text-xs font-semibold text-[#64748B]">
                <span className="flex items-center gap-1.5"><User size={13} className="text-neutral-400" /> Criador: <strong className="text-neutral-800">{idea.creator}</strong></span>
                <span className="text-neutral-300">•</span>
                <span className="flex items-center gap-1.5"><User size={13} className="text-neutral-400" /> Responsável: <strong className="text-neutral-800">{idea.owner}</strong></span>
                <span className="text-neutral-300">•</span>
                <span className="flex items-center gap-1.5"><Calendar size={13} className="text-neutral-400" /> Prazo: {idea.estimatedTime}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2.5 sm:self-end lg:self-center">
            <button onClick={() => setIsEditModalOpen(true)} className="p-2 bg-white text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 rounded-xl transition-all border border-neutral-200 shadow-sm cursor-pointer" title="Editar"><Edit size={14} /></button>
            <button onClick={handleDeleteIdea} className="p-2 bg-white text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all border border-red-200 shadow-sm cursor-pointer" title="Excluir ideia"><Trash2 size={14} /></button>
            <button className="p-2 bg-white text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50 rounded-xl transition-all border border-neutral-200 shadow-sm cursor-pointer" title="Compartilhar"><Share2 size={14} /></button>
            {isConvertedToProject ? (
              <span className="px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs font-bold rounded-xl flex items-center gap-1.5"><CheckCircle2 size={14} /> Convertido em Projeto</span>
            ) : (
              <button onClick={handleConvertToProject} className="px-4 py-2 text-xs font-bold bg-neutral-900 text-white hover:bg-black rounded-xl transition-all shadow-sm cursor-pointer flex items-center gap-1.5"><Rocket size={13} /> Converter em Projeto</button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-[#0F172A0A] pt-6">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-black uppercase tracking-wider text-neutral-400">Score de Inovação</span>
            <div className="flex items-center gap-2">
              <span className="text-lg font-black text-indigo-600">{idea.score}%</span>
              <div className="flex-1 bg-neutral-100 rounded-full h-2 overflow-hidden max-w-[80px]"><div className="bg-indigo-600 h-full rounded-full" style={{ width: `${idea.score}%` }}></div></div>
            </div>
          </div>
          <div className="flex flex-col gap-1"><span className="text-[10px] font-black uppercase tracking-wider text-neutral-400">ROI Estimado</span><span className="text-sm font-bold text-emerald-600 flex items-center gap-1"><TrendingUp size={14} /> {idea.roi}</span></div>
          <div className="flex flex-col gap-1"><span className="text-[10px] font-black uppercase tracking-wider text-neutral-400">Empresa</span><span className="text-sm font-bold text-neutral-800">{idea.company}</span></div>
          <div className="flex flex-col gap-1"><span className="text-[10px] font-black uppercase tracking-wider text-neutral-400">Criado em</span><span className="text-sm font-bold text-neutral-800">{new Date(idea.createdAt).toLocaleDateString('pt-BR')}</span></div>
        </div>
      </div>

      {/* Modal de Edição */}
      <EditIdeaModal 
        isOpen={isEditModalOpen} 
        onClose={() => setIsEditModalOpen(false)} 
        onSuccess={() => {
          setIsEditModalOpen(false);
          // Recarregar dados da ideia
        }}
        idea={idea}
      />

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━
          TABULAR NAVIGATION
          ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="overflow-x-auto whitespace-nowrap pb-1 scrollbar-none flex gap-1 bg-white p-1 rounded-2xl border border-neutral-100 shadow-sm max-w-full">
        {[
          { id: 'overview', label: 'Overview' }, { id: 'description', label: 'Descrição' }, { id: 'canvas', label: 'Canvas' },
          { id: 'feasibility', label: 'Viabilidade' }, { id: 'discussions', label: 'Atividades' }, { id: 'files', label: 'Arquivos' },
          { id: 'roadmap', label: 'Roadmap' }, { id: 'project', label: 'Projeto' }, { id: 'ia', label: 'IA Cyzor' }, { id: 'history', label: 'Histórico' }
        ].map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${activeTab === tab.id ? 'bg-neutral-900 text-white shadow-xs' : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB PANELS */}
      <div className="w-full min-h-[400px]">
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div key="overview" {...tabAnimation} className="w-full">
              <div className="flex flex-col gap-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {[
                    { label: 'Innovation Score', val: `${idea.score}%`, desc: 'Originalidade & Viabilidade', icon: Sparkles, color: 'text-indigo-600 bg-indigo-50 border-indigo-100' },
                    { label: 'ROI Estimado', val: idea.roi, desc: 'Multiplicador de valor', icon: Coins, color: 'text-emerald-600 bg-emerald-50 border-emerald-100' },
                    { label: 'Impacto Global', val: idea.impact, desc: 'Relevância operacional', icon: TrendingUp, color: 'text-amber-600 bg-amber-50 border-amber-100' },
                    { label: 'Esforço de Dev', val: idea.effort, desc: 'Recursos alocados', icon: Activity, color: 'text-blue-600 bg-blue-50 border-blue-100' }
                  ].map((stat, idx) => {
                    const StatIcon = stat.icon;
                    return (
                      <div key={idx} className="bg-white border border-neutral-100 p-5 rounded-2xl flex items-center justify-between shadow-xs">
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] font-extrabold text-neutral-400 uppercase tracking-widest">{stat.label}</span>
                          <span className="text-xl font-black text-neutral-900 leading-tight">{stat.val}</span>
                          <span className="text-[9px] text-neutral-500 font-semibold">{stat.desc}</span>
                        </div>
                        <div className={`p-2.5 rounded-xl border ${stat.color}`}><StatIcon size={16} /></div>
                      </div>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 flex flex-col gap-6">
                    <div className="bg-white border border-neutral-100 p-6 rounded-2xl flex flex-col gap-4 shadow-xs">
                      <h3 className="text-xs font-black uppercase text-neutral-400 tracking-wider">Resumo Executivo</h3>
                      <p className="text-sm font-semibold text-neutral-700 leading-relaxed">{idea.description || idea.objective || 'Descrição detalhada da ideia de inovação.'}</p>
                      <div className="border-t border-neutral-100 pt-4 flex flex-col gap-3">
                        <h4 className="text-[11px] font-extrabold uppercase text-neutral-400">Objetivo Estratégico</h4>
                        <p className="text-xs font-semibold text-neutral-600">{idea.objective}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="bg-white border border-neutral-100 p-5 rounded-2xl flex flex-col gap-3 shadow-xs">
                        <div className="flex items-center gap-2 text-rose-600"><AlertTriangle size={15} /><h4 className="text-xs font-black uppercase tracking-wider">Problema Raiz</h4></div>
                        <p className="text-xs text-neutral-600 leading-relaxed font-semibold">{idea.problem}</p>
                      </div>
                      <div className="bg-white border border-neutral-100 p-5 rounded-2xl flex flex-col gap-3 shadow-xs">
                        <div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 size={15} /><h4 className="text-xs font-black uppercase tracking-wider">Solução Proposta</h4></div>
                        <p className="text-xs text-neutral-600 leading-relaxed font-semibold">{idea.solution}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-5">
                    <div className="bg-white border border-neutral-100 p-6 rounded-2xl flex flex-col gap-5 shadow-xs">
                      <h3 className="text-xs font-black uppercase text-neutral-400 tracking-wider">Metas de Inovação</h3>
                      <div className="flex flex-col gap-3">
                        {[{ label: 'Alinhamento Estratégico', val: '95%', color: 'w-[95%] bg-indigo-600' }, { label: 'Viabilidade Técnica', val: '88%', color: 'w-[88%] bg-sky-500' }, { label: 'Demanda de Mercado', val: '92%', color: 'w-[92%] bg-emerald-500' }].map((m, idx) => (
                          <div key={idx} className="flex flex-col gap-1.5">
                            <div className="flex justify-between items-center text-xs font-bold text-neutral-700"><span>{m.label}</span><span className="text-indigo-600">{m.val}</span></div>
                            <div className="w-full h-1.5 bg-neutral-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${m.color}`} /></div>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-neutral-100 pt-4 flex flex-col gap-3.5">
                        <div className="flex items-center justify-between text-xs"><span className="text-neutral-400 font-bold">Complexidade</span><span className="font-extrabold text-neutral-700">{idea.complexity}</span></div>
                        <div className="flex items-center justify-between text-xs"><span className="text-neutral-400 font-bold">Risco</span><span className="font-extrabold text-neutral-700">{idea.risk}</span></div>
                        <div className="flex items-center justify-between text-xs"><span className="text-neutral-400 font-bold">Tempo de Go-to-Market</span><span className="font-extrabold text-neutral-700">{idea.estimatedTime}</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'description' && (
            <motion.div key="description" {...tabAnimation} className="w-full">
              <div className="bg-white border border-neutral-100 p-6 sm:p-8 rounded-2xl shadow-xs flex flex-col gap-6">
                <div className="flex items-center gap-2"><FileText size={18} className="text-indigo-600" /><h3 className="text-sm font-black uppercase text-neutral-900 tracking-wider">Estruturação Detalhada</h3></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-2">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2"><h4 className="text-xs font-black uppercase text-neutral-400 tracking-wide">1. Descrição do Problema</h4><p className="text-xs sm:text-sm text-neutral-700 font-semibold leading-relaxed">{idea.problem}</p></div>
                    <div className="flex flex-col gap-2"><h4 className="text-xs font-black uppercase text-neutral-400 tracking-wide">2. Objetivo Principal</h4><p className="text-xs sm:text-sm text-neutral-700 font-semibold leading-relaxed">{idea.objective}</p></div>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2"><h4 className="text-xs font-black uppercase text-indigo-600 tracking-wide">3. Solução Proposta</h4><p className="text-xs sm:text-sm text-neutral-700 font-semibold leading-relaxed">{idea.solution}</p></div>
                    <div className="flex flex-col gap-2"><h4 className="text-xs font-black uppercase text-indigo-600 tracking-wide">4. Principais Benefícios</h4><ul className="list-disc list-inside flex flex-col gap-1.5 text-xs sm:text-sm text-neutral-700 font-semibold">{(idea.benefits || []).map((b: string, i: number) => (<li key={i}>{b}</li>))}</ul></div>
                  </div>
                </div>
                <div className="border-t border-neutral-100 pt-6 mt-4"><h4 className="text-xs font-black uppercase text-neutral-400 tracking-wide mb-2">5. Resultados e Métricas Esperadas</h4><p className="text-xs sm:text-sm text-neutral-700 font-semibold leading-relaxed">{idea.outcomes}</p></div>
              </div>
            </motion.div>
          )}

          {activeTab === 'canvas' && (
            <motion.div key="canvas" {...tabAnimation} className="w-full">
              <div className="flex flex-col gap-5">
                <div className="flex justify-between items-center bg-white px-6 py-4 rounded-xl border border-neutral-100 shadow-xs">
                  <div className="flex items-center gap-2"><Layers size={16} className="text-indigo-600" /><span className="text-xs font-black uppercase text-neutral-900 tracking-wider">Mini Lean Canvas de Inovação</span></div>
                  <span className="text-[10px] text-neutral-400 font-semibold">Metodologia Ágil de Modelagem de Negócio</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-2.5"><span className="text-[10px] font-black uppercase text-rose-500 tracking-wider">Problema Raiz</span><p className="text-xs text-neutral-600 leading-relaxed font-semibold">{idea.canvas.problem || 'Problema a ser mapeado.'}</p></div>
                  <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-2.5"><span className="text-[10px] font-black uppercase text-indigo-600 tracking-wider">Solução Inovadora</span><p className="text-xs text-neutral-600 leading-relaxed font-semibold">{idea.canvas.solution || 'Solução a ser mapeada.'}</p></div>
                  <div className="bg-indigo-50/50 p-5 rounded-2xl border border-indigo-100 shadow-xs flex flex-col gap-2.5"><span className="text-[10px] font-black uppercase text-indigo-700 tracking-wider">Proposta Única</span><p className="text-xs text-indigo-900 leading-relaxed font-extrabold">{idea.canvas.valueProp || 'Proposta de valor a ser definida.'}</p></div>
                  <div className="bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-2.5"><span className="text-[10px] font-black uppercase text-amber-600 tracking-wider">Vantagem Competitiva</span><p className="text-xs text-neutral-600 leading-relaxed font-semibold">{idea.canvas.advantage || 'Vantagem a ser mapeada.'}</p></div>
                  <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-2.5"><span className="text-[10px] font-black uppercase text-neutral-600 tracking-wider">Segmentos de Clientes</span><p className="text-xs text-neutral-600 leading-relaxed font-semibold">{idea.canvas.segments || 'Segmentos a serem mapeados.'}</p></div>
                  <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-2.5"><span className="text-[10px] font-black uppercase text-neutral-600 tracking-wider">Métricas de Sucesso</span><p className="text-xs text-neutral-600 leading-relaxed font-semibold">{idea.canvas.metrics || 'Métricas a serem definidas.'}</p></div>
                  <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-2.5"><span className="text-[10px] font-black uppercase text-neutral-400 tracking-wider">Estrutura de Custos</span><p className="text-xs text-neutral-600 leading-relaxed font-semibold">{idea.canvas.costs || 'Custos a serem mapeados.'}</p></div>
                  <div className="lg:col-span-3 bg-emerald-50/40 p-5 rounded-2xl border border-emerald-100 shadow-xs flex flex-col gap-2.5"><span className="text-[10px] font-black uppercase text-emerald-700 tracking-wider">Fontes de Receita</span><p className="text-xs text-emerald-900 leading-relaxed font-bold">{idea.canvas.revenues || 'Receitas a serem mapeadas.'}</p></div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'feasibility' && (
            <motion.div key="feasibility" {...tabAnimation} className="w-full">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white p-6 sm:p-8 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-6">
                  <div className="flex items-center gap-2 mb-2"><BarChart3 className="text-indigo-600" size={18} /><h3 className="text-xs font-black uppercase text-neutral-900 tracking-wider">Indicadores de Viabilidade</h3></div>
                  <div className="flex flex-col gap-5">
                    {[{ label: 'Impacto nos Clientes', val: 92, desc: 'Potencial de melhoria na dor do cliente', color: 'bg-emerald-500' }, { label: 'Alinhamento Estratégico', val: 95, desc: 'Frequência de acoplamento com ERP', color: 'bg-indigo-600' }, { label: 'Complexidade de Setup', val: 55, desc: 'Dificuldade de implantação técnica', color: 'bg-amber-500' }, { label: 'Esforço Necessário', val: 68, desc: 'Recursos humanos e computacionais', color: 'bg-sky-500' }, { label: 'Nível de Risco', val: 35, desc: 'Possíveis impactos negativos e barreiras', color: 'bg-rose-500' }].map((ind, idx) => (
                      <div key={idx} className="flex flex-col gap-1">
                        <div className="flex justify-between items-center text-xs font-bold text-neutral-800"><span>{ind.label}</span><span className="font-extrabold">{ind.val}%</span></div>
                        <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${ind.color}`} style={{ width: `${ind.val}%` }} /></div>
                        <span className="text-[10px] text-neutral-400 font-semibold leading-relaxed">{ind.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-6">
                  <h3 className="text-xs font-black uppercase text-neutral-400 tracking-wider">Business Case Matrix</h3>
                  <div className="flex flex-col gap-4">
                    <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-100 flex flex-col gap-1.5"><span className="text-[10px] font-extrabold text-neutral-400 uppercase tracking-widest">ROI Estimado (12m)</span><span className="text-2xl font-black text-emerald-600">{idea.roi}</span><span className="text-[10px] text-neutral-500 font-semibold leading-tight">Retorno sobre o capital técnico alocado estimado em 12 meses após deploy.</span></div>
                    <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-100 flex flex-col gap-1.5"><span className="text-[10px] font-extrabold text-neutral-400 uppercase tracking-widest">Go-to-Market Time</span><span className="text-lg font-bold text-neutral-800">{idea.estimatedTime}</span><span className="text-[10px] text-neutral-500 font-semibold leading-tight">Período de design, desenvolvimento e triagem em beta antes da release final.</span></div>
                    <div className="p-4 bg-indigo-50/50 rounded-xl border border-indigo-100 flex flex-col gap-1.5"><span className="text-[10px] font-extrabold text-indigo-600 uppercase tracking-widest">Cyzor Innovation Grade</span><span className="text-xl font-black text-indigo-700">{idea.score} / 100</span></div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'discussions' && (
            <motion.div key="discussions" {...tabAnimation} className="w-full">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 flex flex-col gap-5">
                  <div className="bg-white p-4 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-3">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Buscar no histórico de atividades... (ex: #ia, autor, texto)" className="w-full pl-9 pr-8 py-2 bg-neutral-50 hover:bg-neutral-100/70 focus:bg-white border border-neutral-200 focus:border-indigo-500 rounded-xl text-xs sm:text-sm text-neutral-800 placeholder-neutral-400 focus:outline-none transition-all font-semibold" />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"><Hash size={14} /></span>
                        {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-neutral-200 hover:bg-neutral-300 text-neutral-600 flex items-center justify-center text-[9px] font-black cursor-pointer">✕</button>}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 items-center justify-between border-t border-neutral-50 pt-2.5">
                      <div className="flex flex-wrap gap-1.5 items-center">
                        <span className="text-[9px] font-black uppercase text-neutral-400 tracking-wider mr-1">Tipo:</span>
                        {[{ id: 'all', label: 'Tudo' }, { id: 'decision', label: '⚡ Decisões' }, { id: 'suggestion', label: '💡 Sugestões' }, { id: 'ai', label: '🤖 Insights IA' }, { id: 'indicator', label: '📈 Indicadores' }, { id: 'comment', label: '💬 Comentários' }].map(f => (
                          <button key={f.id} onClick={() => setDiscussionFilterType(f.id)} className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1 cursor-pointer ${discussionFilterType === f.id ? 'bg-neutral-900 text-white shadow-2xs' : 'bg-neutral-50 hover:bg-neutral-100 text-neutral-600'}`}>
                            {f.label}
                          </button>
                        ))}
                      </div>
                      {discussionFilterTag && <button onClick={() => setDiscussionFilterTag(null)} className="px-2 py-0.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-[9px] font-extrabold rounded-md border border-indigo-100 flex items-center gap-1 cursor-pointer"><span>Tag: {discussionFilterTag}</span><span className="font-bold">✕</span></button>}
                    </div>
                  </div>
                  <div className="bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-4">
                    <div className="flex gap-3 items-start">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-700 font-black text-sm flex items-center justify-center overflow-hidden shrink-0 shadow-inner">
                        {getMemberPhoto('João Silva') ? <img src={getMemberPhoto('João Silva')!} alt="JS" className="w-full h-full object-cover" /> : 'JS'}
                      </div>
                      <div className="flex-1 flex flex-col gap-3">
                        <textarea value={newCommentText} onChange={(e) => setNewCommentText(e.target.value)} placeholder="Publique uma nova atividade... Use #hashtags para contextualizar." rows={3} className="w-full p-3.5 bg-neutral-50 hover:bg-neutral-100/50 focus:bg-white border border-neutral-200 focus:border-indigo-500 rounded-2xl text-xs sm:text-sm text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 font-semibold transition-all resize-none" />
                        <div className="flex flex-wrap gap-1.5 items-center">
                          <span className="text-[9px] font-black text-neutral-400 uppercase tracking-wider mr-1">Tags sugeridas:</span>
                          {['#engenharia', '#produto', '#mvp', '#arquitetura', '#validação', '#pricing', '#ia'].map(tag => {
                            const isSelected = selectedNewCommentTags.includes(tag);
                            return (<button key={tag} type="button" onClick={() => { isSelected ? setSelectedNewCommentTags(prev => prev.filter(t => t !== tag)) : setSelectedNewCommentTags(prev => [...prev, tag]); }} className={`px-2 py-1 text-[10px] font-bold rounded-lg transition-all cursor-pointer border ${isSelected ? 'bg-indigo-50 border-indigo-200 text-indigo-700 font-extrabold' : 'bg-neutral-50 hover:bg-neutral-100 border-neutral-200/60 text-neutral-500'}`}>{tag}</button>);
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-neutral-100 pt-3 flex flex-wrap gap-3 items-center justify-between">
                      <div className="flex gap-1">
                        {[{ id: 'comment', label: 'Comentário', color: 'hover:bg-neutral-100 hover:text-neutral-700 text-neutral-500 border-neutral-200' }, { id: 'suggestion', label: 'Sugestão 💡', color: 'hover:bg-amber-50 hover:text-amber-700 text-neutral-500 border-neutral-200' }, { id: 'decision', label: 'Decisão ⚡', color: 'hover:bg-purple-50 hover:text-purple-700 text-neutral-500 border-neutral-200' }].map(t => (
                          <button key={t.id} type="button" onClick={() => setCommentType(t.id as any)} className={`px-3 py-1.5 text-[10px] font-extrabold uppercase rounded-xl border cursor-pointer transition-all flex items-center gap-1.5 ${commentType === t.id ? commentType === 'decision' ? 'bg-purple-600 text-white border-purple-600 shadow-xs' : commentType === 'suggestion' ? 'bg-amber-500 text-white border-amber-500 shadow-xs' : 'bg-indigo-600 text-white border-indigo-600 shadow-xs' : `bg-neutral-50 ${t.color}`}`}>{t.label}</button>
                        ))}
                      </div>
                      <button onClick={handleAddComment} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 active:scale-98 text-white text-xs font-bold rounded-xl cursor-pointer shadow-sm transition-all flex items-center gap-1.5"><Send size={12} /><span>Publicar</span></button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-6">
                    {filteredComments.length === 0 ? (
                      <div className="bg-white p-10 rounded-2xl border border-neutral-100 text-center flex flex-col items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-neutral-50 flex items-center justify-center text-neutral-400"><MessageSquare size={20} /></div>
                        <span className="text-xs font-black uppercase text-neutral-800">Nenhuma atividade encontrada</span>
                        <span className="text-[10px] text-neutral-400 font-semibold">Tente ajustar a busca ou os filtros de tipo de atividade.</span>
                        {(discussionFilterTag || discussionFilterType !== 'all' || searchQuery) && <button onClick={() => { setDiscussionFilterTag(null); setDiscussionFilterType('all'); setSearchQuery(''); }} className="mt-1 px-3 py-1.5 bg-neutral-100 hover:bg-neutral-200 text-neutral-600 text-[10px] font-extrabold rounded-xl transition-all cursor-pointer">Limpar Filtros e Busca</button>}
                      </div>
                    ) : (
                      (['Hoje', 'Ontem', 'Esta Semana', 'Este Mês', 'Mais Antigos'] as const).map(group => {
                        const groupComments = filteredComments.filter(c => c.timelineGroup === group || (!c.timelineGroup && group === 'Mais Antigos'));
                        if (groupComments.length === 0) return null;
                        return (
                          <div key={group} className="flex flex-col gap-4">
                            <div className="flex items-center gap-3 py-1"><span className="text-[10px] font-extrabold uppercase tracking-widest text-neutral-400 whitespace-nowrap">{group}</span><div className="h-[1px] bg-neutral-100 flex-1" /></div>
                            {groupComments.map((comment) => (
                              <div key={comment.id} className={`bg-white p-5 rounded-2xl border shadow-xs flex flex-col gap-4 relative ${comment.isPinned ? 'border-indigo-100 bg-indigo-50/10' : 'border-neutral-100 hover:border-neutral-200/80'}`}>
                                {comment.isPinned && <div className="absolute top-2.5 right-4 flex items-center gap-1 text-[10px] font-extrabold text-indigo-600"><Pin size={10} className="fill-indigo-600 rotate-45" /><span>Fixado pelo Time</span></div>}
                                <div className="flex justify-between items-start">
                                  <div className="flex gap-3 items-center">
                                    <div className="w-10 h-10 rounded-full bg-neutral-100 border border-neutral-200 flex items-center justify-center text-xs font-extrabold overflow-hidden shrink-0 shadow-xs relative">
                                      {comment.photo ? <img src={comment.photo} alt={comment.author} className="w-full h-full object-cover" /> : <span className="text-neutral-500 font-extrabold">{comment.author.charAt(0)}</span>}
                                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" />
                                    </div>
                                    <div className="flex flex-col"><span className="text-xs sm:text-sm font-black text-neutral-800 leading-tight">{comment.author}</span><span className="text-[10px] text-neutral-400 font-semibold">{comment.time}</span><span className="text-[10px] text-neutral-400 font-bold">{comment.role}</span></div>
                                  </div>
                                </div>
                                <p className="text-xs sm:text-sm text-neutral-700 font-semibold leading-relaxed">{comment.text}</p>
                                {comment.tags && comment.tags.length > 0 && <div className="flex flex-wrap gap-1">{comment.tags.map(t => (<button key={t} onClick={() => setDiscussionFilterTag(t === discussionFilterTag ? null : t)} className={`px-2 py-0.5 rounded-lg text-[10px] font-bold transition-all cursor-pointer border ${discussionFilterTag === t ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-indigo-50/50 hover:bg-indigo-50 text-indigo-600 border-indigo-100'}`}>{t}</button>))}</div>}
                                <div className="flex items-center gap-3 text-[10px] text-neutral-400 font-bold">
                                  <button onClick={() => handleToggleLikeComment(comment.id)} className="flex items-center gap-1 cursor-pointer"><span>{comment.likedByCurrentUser ? '❤️' : '🤍'}</span><span>{comment.votes}</span></button>
                                  <button onClick={() => handleTogglePin(comment.id)} className="cursor-pointer"><Pin size={13} className={comment.isPinned ? 'fill-indigo-600 rotate-45 text-indigo-600' : ''} /></button>
                                  <button onClick={() => handleShareComment(comment)} className="cursor-pointer"><Share2 size={13} /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-5">
                  <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 p-5 rounded-2xl border border-indigo-100/50 shadow-xs flex flex-col gap-3">
                    <div className="flex items-center gap-1.5"><Sparkles size={14} className="text-indigo-600" /><h3 className="text-xs font-black uppercase text-indigo-900 tracking-wider">Cyzor IA: Resumo de Alinhamento</h3></div>
                    <p className="text-[11px] text-neutral-600 font-semibold leading-relaxed">A ideia tem altíssimo alinhamento estratégico com o ecossistema ERP.</p>
                  </div>
                  <div className="bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-4">
                    <h3 className="text-xs font-black uppercase text-neutral-900 tracking-wider">Métricas do Feed</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-neutral-50 border border-neutral-100 rounded-xl flex flex-col"><span className="text-[9px] font-extrabold text-neutral-400 uppercase tracking-widest">Interações</span><span className="text-lg font-black text-neutral-800 mt-1">{comments.reduce((acc, curr) => acc + (curr.votes || 0), 0)}</span></div>
                      <div className="p-3 bg-indigo-50/30 border border-indigo-100/40 rounded-xl flex flex-col"><span className="text-[9px] font-extrabold text-indigo-500 uppercase tracking-widest">Decisões</span><span className="text-lg font-black text-indigo-700 mt-1">{comments.filter(c => c.type === 'decision').length}</span></div>
                    </div>
                  </div>
                  <div className="bg-white p-5 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-3">
                    <h3 className="text-xs font-black uppercase text-neutral-900 tracking-wider">Tópicos Populares</h3>
                    <div className="flex flex-col gap-1.5 mt-1">
                      {popularTags.length === 0 ? <span className="text-[10px] text-neutral-400 font-semibold italic">Nenhuma hashtag ativa</span> : popularTags.map(({ tag, count }) => (
                        <button key={tag} onClick={() => setDiscussionFilterTag(discussionFilterTag === tag ? null : tag)} className={`flex justify-between items-center px-2.5 py-1.5 rounded-lg text-left text-xs transition-all cursor-pointer ${discussionFilterTag === tag ? 'bg-indigo-600 text-white font-black' : 'bg-neutral-50 hover:bg-neutral-100 text-neutral-700 font-semibold'}`}>
                          <span>{tag}</span><span className="text-[10px] px-1.5 py-0.2 rounded-full font-extrabold bg-neutral-200/60 text-neutral-500">{count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'files' && (
            <motion.div key="files" {...tabAnimation} className="w-full">
              <div className="flex flex-col gap-5">
                <div className="bg-white p-8 rounded-2xl border border-dashed border-neutral-200 hover:border-neutral-300 flex flex-col items-center justify-center gap-3 transition-colors relative cursor-pointer group shadow-xs">
                  <input type="file" onChange={handleFileUpload} className="absolute inset-0 opacity-0 cursor-pointer" aria-label="Upload de arquivos" />
                  <div className="p-3 bg-indigo-50 text-indigo-600 rounded-full group-hover:scale-105 transition-transform"><Upload size={18} /></div>
                  <span className="text-xs font-bold text-neutral-800">Arraste seu arquivo aqui ou clique para selecionar</span>
                  <span className="text-[10px] text-neutral-400 font-semibold">Suporte a PDF, Imagens, DOC, Planilhas (máx. 10MB)</span>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-4">
                  <h3 className="text-xs font-black uppercase text-neutral-900 tracking-wider">Anexos Vinculados ({allFiles.length})</h3>
                  <div className="flex flex-col gap-2">
                    {allFiles.length === 0 && <div className="text-xs text-neutral-400 font-semibold text-center py-4">Nenhum arquivo anexado a esta ideia.</div>}
                    {allFiles.map((file) => (
                      <div key={file.id} className="p-4 rounded-xl border border-neutral-100 hover:bg-neutral-50 flex items-center justify-between transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><FileText size={15} /></div>
                          <div className="flex flex-col"><span className="text-xs font-bold text-neutral-800">{file.name}</span><span className="text-[10px] text-neutral-400 font-semibold">Tamanho: {file.size} • Adicionado por: {file.addedBy} em {file.date}</span></div>
                        </div>
                        <button className="p-2 text-neutral-400 hover:text-neutral-600 cursor-pointer" title="Fazer Download"><Download size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'roadmap' && (
            <motion.div key="roadmap" {...tabAnimation} className="w-full">
              <div className="bg-white p-6 sm:p-8 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-6">
                <div className="flex justify-between items-center border-b border-neutral-100 pb-4">
                  <div className="flex items-center gap-2"><Compass size={18} className="text-indigo-600 animate-spin" style={{ animationDuration: '6s' }} /><h3 className="text-xs font-black uppercase text-neutral-900 tracking-wider">Evolution Pipeline & Roadmap</h3></div>
                  <span className="text-[10px] text-neutral-400 font-semibold">Ciclo de Vida de Inovação</span>
                </div>
                <div className="relative pl-8 border-l-2 border-neutral-100 flex flex-col gap-8 py-3">
                  {[
                    { title: '💡 Ideia Capturada', desc: 'Ideia consolidada no Banco de Inovação.', date: '10 Jul 2026', checked: true },
                    { title: '🔍 Análise Estatística & Viabilidade', desc: 'Score de impacto estimado.', date: '12 Jul 2026', checked: true },
                    { title: '📋 Refinamento & Especificação', desc: 'Detalhamento do Lean Canvas.', date: '18 Jul 2026', checked: true },
                    { title: '💰 Elaboração do Business Case', desc: 'Definição do ROI potencial.', date: '25 Jul 2026', checked: true },
                    { title: '✅ Aprovação e Priorização', desc: 'Aprovada pelo comitê executivo.', date: '28 Jul 2026', checked: true },
                    { title: '🚀 Conversão em Projeto / Desenvolvimento', desc: 'Passagem oficial para o ERP.', date: 'Agora', checked: isConvertedToProject, highlight: true }
                  ].map((milestone, idx) => (
                    <div key={idx} className="relative">
                      <span className={`absolute -left-[41px] top-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center ${milestone.checked ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-neutral-200 text-neutral-300'}`}><CheckCircle2 size={12} /></span>
                      <div className="flex flex-col gap-1">
                        <div className="flex justify-between items-start"><span className={`text-xs font-black ${milestone.highlight ? 'text-indigo-600' : 'text-neutral-800'}`}>{milestone.title}</span><span className="text-[10px] text-neutral-400 font-semibold">{milestone.date}</span></div>
                        <p className="text-xs text-neutral-500 font-semibold leading-relaxed">{milestone.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'project' && (
            <motion.div key="project" {...tabAnimation} className="w-full">
              <div className="flex flex-col gap-5">
                {isConvertedToProject && convertedProjectData ? (
                  <div className="bg-white p-6 sm:p-8 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-6">
                    <div className="flex items-center gap-2.5"><Rocket size={18} className="text-indigo-600" /><h3 className="text-xs font-black uppercase text-neutral-900 tracking-wider">Projeto Integrado & Ativo</h3></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 bg-neutral-50 p-5 rounded-xl border border-neutral-100">
                      <div className="flex flex-col gap-1"><span className="text-[10px] font-extrabold text-neutral-400 uppercase">Nome do Projeto</span><span className="text-xs font-black text-neutral-800">{convertedProjectData.projectName}</span></div>
                      <div className="flex flex-col gap-1"><span className="text-[10px] font-extrabold text-neutral-400">Código ERP</span><span className="text-xs font-black text-indigo-600 font-mono">{convertedProjectData.code}</span></div>
                      <div className="flex flex-col gap-1"><span className="text-[10px] font-extrabold text-neutral-400">Status Executivo</span><span className="text-xs font-bold text-neutral-800">{convertedProjectData.status}</span></div>
                      <div className="flex flex-col gap-1"><span className="text-[10px] font-extrabold text-neutral-400">Sprint Atual</span><span className="text-xs font-bold text-neutral-800 truncate">{convertedProjectData.sprint}</span></div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex justify-between items-center text-xs font-bold text-neutral-800"><span>Progresso do Projeto</span><span>{convertedProjectData.progress}%</span></div>
                      <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden"><div className="h-full rounded-full bg-indigo-600" style={{ width: `${convertedProjectData.progress}%` }} /></div>
                    </div>
                    <div className="border-t border-neutral-100 pt-5 flex justify-between items-center">
                      <div className="flex flex-col gap-1"><span className="text-[10px] text-neutral-400 font-extrabold uppercase">Equipe Alocada</span><div className="flex items-center -space-x-1.5 mt-1">{convertedProjectData.members.map((m: string, idx: number) => (<div key={idx} className="w-[22px] h-[22px] rounded-full bg-white border border-neutral-200 flex items-center justify-center overflow-hidden" title={m}>{getMemberPhoto(m) ? <img src={getMemberPhoto(m)!} alt={m} className="w-full h-full object-cover" /> : m.charAt(0)}</div>))}</div></div>
                      <button onClick={() => { setGlobalFilters({ ...globalFilters, projectId: 1 }); setAppView('projeto-workspace'); }} className="px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl flex items-center gap-1 cursor-pointer">Ir para Workspace do Projeto <ChevronRight size={12} /></button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white p-8 rounded-2xl border border-neutral-100 shadow-xs flex flex-col items-center justify-center text-center gap-4">
                    <div className="p-3 bg-indigo-50 text-indigo-600 rounded-full"><Rocket size={24} className="animate-bounce" /></div>
                    <h3 className="text-sm font-black uppercase text-neutral-800">Conversão de Ideia para Projeto</h3>
                    <p className="text-xs text-neutral-500 font-semibold leading-relaxed">Ao converter esta ideia aprovada, um workspace de projeto executivo será inicializado automaticamente.</p>
                    <button onClick={handleConvertToProject} className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-extrabold rounded-xl cursor-pointer">Converter em Projeto ERP Agora</button>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'ia' && (
            <motion.div key="ia" {...tabAnimation} className="w-full">
              <div className="flex flex-col gap-6">
                <div className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white p-6 sm:p-8 rounded-2xl border border-indigo-900 shadow-lg relative overflow-hidden">
                  <div className="absolute right-0 top-0 w-72 h-72 bg-indigo-500/10 blur-[80px] rounded-full pointer-events-none" />
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2"><Sparkles size={16} className="text-indigo-400 animate-pulse" /><span className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-300">Cyzor Neural Core</span></div>
                      <h3 className="text-lg sm:text-xl font-black">Inteligência Co-Piloto de Inovação</h3>
                      <p className="text-xs text-indigo-200/80 font-medium">Análise profunda automatizada de riscos, viabilidade técnica e melhorias.</p>
                    </div>
                    <button onClick={handleAiAnalysis} disabled={isAiAnalyzing} className="px-5 py-2.5 bg-white text-slate-950 hover:bg-slate-100 disabled:opacity-50 text-xs font-black rounded-xl shadow-md cursor-pointer shrink-0 transition-transform flex items-center gap-2 active:scale-95">
                      {isAiAnalyzing ? <><div className="w-3.5 h-3.5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" /> Analisando...</> : <><Brain size={14} className="text-indigo-600 animate-pulse" /> Analisar com Cyzor Neural</>}
                    </button>
                  </div>
                </div>
                {isAiAnalyzing && <div className="p-12 bg-white rounded-2xl border border-neutral-100 shadow-xs flex flex-col items-center justify-center gap-4"><div className="w-16 h-16 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin" /><span className="text-xs font-black uppercase text-neutral-800">Processando...</span></div>}
                {!isAiAnalyzing && aiAnalysisResult && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 flex flex-col gap-6">
                      <div className="bg-white p-6 rounded-2xl border border-neutral-100 shadow-xs"><h4 className="text-xs font-black uppercase text-indigo-600 tracking-wider mb-2">Resumo Inteligente</h4><p className="text-xs sm:text-sm text-neutral-700 font-semibold leading-relaxed">{aiAnalysisResult.summary}</p></div>
                      <div className="bg-white p-6 rounded-2xl border border-neutral-100 shadow-xs"><h4 className="text-xs font-black uppercase text-indigo-600 tracking-wider mb-2">Sugestões</h4><div className="flex flex-col gap-3">{aiAnalysisResult.suggestions.map((s: string, idx: number) => (<div key={idx} className="flex gap-2 items-start text-xs text-neutral-700 font-semibold"><span className="p-1 bg-indigo-50 text-indigo-600 rounded-md font-bold shrink-0">{idx + 1}</span><span>{s}</span></div>))}</div></div>
                    </div>
                    <div className="flex flex-col gap-6">
                      <div className="bg-white p-6 rounded-2xl border border-neutral-100 shadow-xs"><h4 className="text-xs font-black uppercase text-neutral-400 tracking-wider mb-2">Métricas</h4><div className="flex flex-col gap-3.5">{Object.entries(aiAnalysisResult.scoreBreakdown).map(([k, v]: [string, any]) => (<div key={k} className="flex flex-col gap-1"><div className="flex justify-between items-center text-xs font-bold text-neutral-800"><span>{k}</span><span className="text-indigo-600">{v}%</span></div><div className="w-full h-1 bg-neutral-100 rounded-full"><div className="h-full rounded-full bg-indigo-600" style={{ width: `${v}%` }} /></div></div>))}</div></div>
                      <div className="bg-white p-6 rounded-2xl border border-neutral-100 shadow-xs"><h4 className="text-xs font-black uppercase text-rose-500 tracking-wider mb-2">Riscos</h4>{aiAnalysisResult.riscos.map((r: string, idx: number) => (<div key={idx} className="flex gap-2 items-start text-xs text-neutral-600 font-semibold mt-2"><AlertTriangle size={12} className="text-rose-500 mt-0.5 shrink-0" /><span>{r}</span></div>))}</div>
                    </div>
                  </div>
                )}
                {!isAiAnalyzing && !aiAnalysisResult && <div className="bg-white p-8 rounded-2xl border border-neutral-100 shadow-xs flex flex-col items-center justify-center gap-3"><Brain size={24} className="text-neutral-400" /><span className="text-xs font-bold text-neutral-800">Pronto para Análise Estatística</span></div>}
              </div>
            </motion.div>
          )}

          {activeTab === 'history' && (
            <motion.div key="history" {...tabAnimation} className="w-full">
              <div className="bg-white p-6 sm:p-8 rounded-2xl border border-neutral-100 shadow-xs flex flex-col gap-5">
                <div className="flex items-center gap-2 border-b border-neutral-100 pb-4 mb-2"><Clock size={18} className="text-indigo-600" /><h3 className="text-xs font-black uppercase text-neutral-900 tracking-wider">Histórico de Atividades</h3></div>
                <div className="flex flex-col gap-5 relative pl-4 border-l border-neutral-100 py-2">
                  {historyLogs.length === 0 && <div className="text-xs text-neutral-400 font-semibold text-center py-4">Nenhum histórico de atividade registrado ainda.</div>}
                  {historyLogs.map((log: any) => (
                    <div key={log.id} className="relative flex flex-col gap-1">
                      <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-indigo-600" />
                      <div className="flex justify-between items-start text-xs"><span className="font-extrabold text-neutral-800">{log.action}</span><span className="text-[10px] text-neutral-400 font-semibold">{log.time}</span></div>
                      <span className="text-[10px] text-neutral-400 font-semibold">Realizado por: <strong className="text-neutral-600">{log.user}</strong></span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Dynamic Toast Feedback Notification */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div initial={{ opacity: 0, y: 50, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }} className="fixed bottom-6 right-6 z-50 bg-neutral-900 text-white text-xs font-bold px-4 py-3 rounded-xl shadow-xl border border-neutral-800 flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
            <span>{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
